// M004-F0746 (WebUI #185, design section 7) - the pure OCR model of a receipt: the five chip states, the form a suggestion pre-fills, the
// bodies Save and Confirm send, and the polling schedule. Nothing here commits anything: a suggestion is only ever a STARTING POINT for the
// form; it becomes the receipt's confirmed fields when the user presses Confirm (`confirm_ocr: true`) or saves edited values.
import { formatAmount, parseAmountInput } from './envelopeModel';

export const OCR_STATUSES = ['queued', 'reading', 'suggested', 'confirmed', 'failed', 'unavailable'];

export const ocrStatusOf = (receipt) => {
  const status = receipt?.ocr?.status;
  return OCR_STATUSES.includes(status) ? status : null;
};

// What the drawer says under the chip, per status (the table of section 7; the manual-entry truth is stated, never hidden).
export const OCR_NOTES = {
  queued: 'This receipt is waiting to be read. You can enter the details yourself at any time.',
  reading: 'This receipt is being read. You can enter the details yourself at any time.',
  suggested: 'We read this receipt. Check the details below and press Confirm. Nothing counts in the totals until you do.',
  confirmed: 'Confirmed. If you change a detail the edit is journalled.',
  failed: 'We could not read this receipt. Enter the details yourself, or try reading it again.',
  unavailable: 'Automatic reading is off, so enter the details yourself.',
};

// ---- polling (section 7: 2 s, backing off to 10 s, only while a receipt is queued or reading; no push channel) -----------------------

export const POLL_FIRST_MS = 2000;
export const POLL_MAX_MS = 10000;
export const isReading = (receipt) => ['queued', 'reading'].includes(ocrStatusOf(receipt));
export const needsPolling = (receipts) => (Array.isArray(receipts) ? receipts : []).some(isReading);
export const pollDelay = (attempt) => Math.min(POLL_MAX_MS, Math.round(POLL_FIRST_MS * (1.5 ** Math.max(0, attempt))));
// Any change here means the read made progress, so the back-off starts again at 2 s.
export const pollSignature = (receipts) => (Array.isArray(receipts) ? receipts : []).map((r) => `${r.receipt_document_id}:${ocrStatusOf(r) || '-'}`).join('|');

// ---- the form -----------------------------------------------------------------------------------------------------------------------

const money = (minor) => (Number.isFinite(Number(minor)) && minor !== null && minor !== undefined ? `${Math.floor(Math.abs(minor) / 100)}.${String(Math.abs(minor) % 100).padStart(2, '0')}` : '');

// The confirmed values of a receipt as form text.
export const confirmedForm = (receipt) => ({
  merchant: receipt?.merchant || '',
  receipt_date: receipt?.receipt_date || '',
  amount: formatAmount(receipt?.amount),
  vat: money(receipt?.vat_minor ?? receipt?.amount?.vat_minor),
  reference: receipt?.reference || '',
});

// The OCR suggestion as form text (empty for any field the engine did not read).
export const suggestionForm = (receipt) => {
  const s = receipt?.ocr?.suggestion;
  if (!s) return null;
  return {
    merchant: s.merchant || '',
    receipt_date: s.receipt_date || '',
    amount: s.total ? formatAmount(s.total) : '',
    vat: money(s.vat_minor ?? s.total?.vat_minor),
    reference: s.reference || '',
  };
};

export const FORM_FIELDS = ['merchant', 'receipt_date', 'amount', 'vat', 'reference'];

// The form a drawer opens with: confirmed values win; while the status is `suggested` an empty field shows the suggestion. `suggestedKeys`
// are the fields currently showing an unconfirmed suggestion (rendered marked, "Suggested").
export const initialForm = (receipt) => {
  const form = confirmedForm(receipt);
  const suggestedKeys = [];
  if (ocrStatusOf(receipt) === 'suggested') {
    const suggestion = suggestionForm(receipt);
    if (suggestion) FORM_FIELDS.forEach((key) => { if (!form[key] && suggestion[key]) { form[key] = suggestion[key]; suggestedKeys.push(key); } });
  }
  return { form, suggestedKeys };
};

export const parseVat = (text) => {
  if (String(text || '').trim() === '') return { ok: true, minor: null };
  const parsed = /^\s*(\d{1,10})(?:\.(\d{1,2}))?\s*$/.exec(String(text));
  return parsed ? { ok: true, minor: Number(parsed[1] + (parsed[2] || '').padEnd(2, '0')) } : { ok: false, minor: null };
};
export const validDate = (text) => text === '' || (/^\d{4}-\d{2}-\d{2}$/.test(text) && !Number.isNaN(Date.parse(`${text}T00:00:00Z`)));

export const validateForm = (form, defaultCurrency) => {
  const amount = form.amount.trim() === '' ? null : parseAmountInput(form.amount, defaultCurrency);
  const vat = parseVat(form.vat);
  return {
    amount, vat,
    amountOk: form.amount.trim() === '' || Boolean(amount),
    vatOk: vat.ok,
    dateOk: validDate(form.receipt_date),
    ok: (form.amount.trim() === '' || Boolean(amount)) && vat.ok && validDate(form.receipt_date),
  };
};

// The body of envelope.receipt.update. Only fields the user EDITED travel (a pre-filled suggestion the user did not touch is not
// sent: on Confirm the Ledger takes it from its own suggestion). `confirm` adds `confirm_ocr: true`; it is added by the Confirm button only.
export const buildBody = ({ form, touched, note, noteChanged, defaultCurrency, confirm = false, alwaysAmount = false }) => {
  const checked = validateForm(form, defaultCurrency);
  const body = {};
  if (noteChanged) body.note = note;
  if (touched.has('amount') || alwaysAmount) body.amount = checked.amount ? { minor: checked.amount.minor, currency: checked.amount.currency } : null;
  if (touched.has('merchant')) body.merchant = form.merchant.trim() || null;
  if (touched.has('receipt_date')) body.receipt_date = form.receipt_date || null;
  if (touched.has('vat')) body.vat_minor = checked.vat.minor;
  if (touched.has('reference')) body.reference = form.reference.trim() || null;
  if (confirm) body.confirm_ocr = true;
  return body;
};

// ---- confidence (T-M004-ENVUX-023, #241) ----------------------------------------------------------------------------------------------

// Below this the drawer adds the low-confidence marker. Strictly below: 0.80 itself is not low.
export const LOW_CONFIDENCE_THRESHOLD = 0.8;
export const LOW_CONFIDENCE_TEXT = 'Low confidence — check against the receipt';

// The suggestion's confidence as a number in [0, 1], or null when the Ledger sent none (never a default 0).
export const suggestionConfidence = (receipt) => {
  const raw = receipt?.ocr?.suggestion?.confidence;
  if (raw === null || raw === undefined || raw === '') return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
};

export const confidenceText = (value) => `OCR ${Math.round(value * 100)}% sure`;
export const isLowConfidence = (value) => value < LOW_CONFIDENCE_THRESHOLD;
